// You will go to the Dagobah system.

import { assert } from "@glideapps/ts-necessities";
import { SubscribableValueProducer } from "common/components/component-helpers";
import { PotentialValue, ValueProducer } from "common/components/computation-types";
import { AppOther, AppEnvironment } from "common/components/types";
import { markComponentDirty } from "common/components/uis";
import * as React from "react";
import { useForceUpdate } from "./use-force-update";

type FastSet<T> = T | Set<T> | undefined;

function fastSetHas<T>(set: FastSet<T>, x: T): boolean {
    if (set === undefined) return false;
    if (set instanceof Set) return set.has(x);
    return set === x;
}

function fastSetAdd<T>(set: FastSet<T>, x: T): FastSet<T> {
    if (set === undefined) return x;
    if (!(set instanceof Set)) {
        if (set === x) return set;

        const s = new Set<T>();
        s.add(set);
        set = s;
    }
    set.add(x);
    return set;
}

function fastSetIsEmpty<T>(set: FastSet<T>): boolean {
    if (set === undefined) return true;
    if (set instanceof Set) return set.size === 0;
    return false;
}

function fastSetClear<T>(set: FastSet<T>): FastSet<T> {
    if (set instanceof Set) {
        set.clear();
        return set;
    }
    return undefined;
}

function useForceInner<O, A>() {
    const forceUpdate = useForceUpdate();
    const fromDataRef = React.useRef(false);
    const runningRef = React.useRef(false);

    const self = React.useMemo(
        () => ({
            forceUpdateFromData: () => {
                fromDataRef.current = true;
                forceUpdate();
            },
            setDirty: (_sender: ValueProducer<O, A>): void => {
                markComponentDirty(self);
            },
        }),
        [forceUpdate]
    );

    // This will run after every render unless re-rendered due to mid render state update
    const cleanup = React.useCallback(() => {
        const oldVPs = subscribedToValueProducersRef.current;
        if (!runningRef.current) return;
        // assert(runningRef.current);
        runningRef.current = false;
        fromDataRef.current = false;
        if (oldVPs instanceof Set) {
            for (const vp of oldVPs) {
                if (!fastSetHas(newValueProducersRef.current, vp)) {
                    vp.unsubscribe(self);
                }
            }
        } else if (oldVPs !== undefined) {
            if (!fastSetHas(newValueProducersRef.current, oldVPs)) {
                oldVPs.unsubscribe(self);
            }
        }

        const old = subscribedToValueProducersRef.current;
        subscribedToValueProducersRef.current = newValueProducersRef.current;
        newValueProducersRef.current = fastSetClear(old);
    }, [self]);
    React.useEffect(cleanup);

    const fromDataCurrent = fromDataRef.current;

    const subscribedToValueProducersRef = React.useRef<FastSet<ValueProducer<O, A>>>();
    const newValueProducersRef = React.useRef<FastSet<ValueProducer<O, A>>>();
    if (runningRef.current === true || !fastSetIsEmpty(newValueProducersRef.current)) {
        cleanup();
    }
    runningRef.current = true;

    // cleanup subscriptions on unmount
    React.useEffect(() => {
        return () => {
            if (subscribedToValueProducersRef.current instanceof Set) {
                for (const vp of subscribedToValueProducersRef.current) {
                    vp.unsubscribe(self);
                }
            } else if (subscribedToValueProducersRef.current !== undefined) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                subscribedToValueProducersRef.current.unsubscribe(self);
            }
            subscribedToValueProducersRef.current = fastSetClear(subscribedToValueProducersRef.current);
        };
    }, [self]);

    // We don't use `useCallback` here on purpose.  `cleanup` above depends on
    // all VPs being forced on every render, and if we keep returning the same
    // `force` function, this might not happen in components that use
    // `useMemo`.  See https://github.com/quicktype/glide/issues/10191
    const force = (vp: ValueProducer<O, A>): PotentialValue<O, A> => {
        if (!runningRef.current) return vp.current;
        if (!fastSetHas(subscribedToValueProducersRef.current, vp)) {
            if (!vp.subscribe(self)) {
                return vp.current;
            }
        }
        newValueProducersRef.current = fastSetAdd(newValueProducersRef.current, vp);

        return vp.current;
    };

    if (subscribedToValueProducersRef.current instanceof Set) {
        for (const vp of subscribedToValueProducersRef.current) {
            if (vp instanceof SubscribableValueProducer) {
                assert(vp.hasSubscribers);
            }
        }
    } else if (subscribedToValueProducersRef.current !== undefined) {
        if (subscribedToValueProducersRef.current instanceof SubscribableValueProducer) {
            assert(subscribedToValueProducersRef.current.hasSubscribers);
        }
    }

    return [force, fromDataCurrent] as const;
}

export function useForce() {
    return useForceInner<AppOther, AppEnvironment>();
}
